import React, { useEffect, useState, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';

const SidebarFlyout = ({ module, iconRect, onMouseEnter, onMouseLeave, onClose, badgeCounts }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [position, setPosition] = useState({ top: 0, left: 68 });
  const flyoutRef = useRef(null);

  useEffect(() => {
    if (!iconRect) return;

    const viewportHeight = window.innerHeight;
    const flyoutHeight = flyoutRef.current?.offsetHeight || 400;
    
    let top = iconRect.top;
    
    if (top + flyoutHeight > viewportHeight - 20) {
      top = Math.max(20, viewportHeight - flyoutHeight - 20);
    }

    setPosition({
      top,
      left: 68,
    });
  }, [iconRect]);

  const isPathActive = (path) => {
    if (!path) return false;
    if (location.pathname === path) return true;
    
    const pathSegments = path.split('/').filter(Boolean);
    const currentSegments = location.pathname.split('/').filter(Boolean);
    
    if (currentSegments.length > pathSegments.length && 
        location.pathname.startsWith(path + '/')) {
      return true;
    }
    
    return false;
  };

  const handleSubItemClick = (path) => {
    navigate(path);
    if (onClose) {
      onClose();
    }
  };

  const Icon = module.icon;
  const hasSubItems = module.subItems && module.subItems.length > 0;

  if (!hasSubItems) {
    return createPortal(
      <div
        ref={flyoutRef}
        className="sidebar-flyout-tooltip"
        style={{
          position: 'fixed',
          top: `${position.top}px`,
          left: `${position.left}px`,
        }}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {module.name}
        <style>{`
          .sidebar-flyout-tooltip {
            background: #1a2e45;
            color: #E8EFF6;
            font-size: 12px;
            padding: 6px 12px;
            border-radius: 8px;
            border: 1px solid rgba(255, 255, 255, 0.08);
            white-space: nowrap;
            z-index: 9999;
            pointer-events: auto;
            animation: flyoutFadeIn 160ms ease-out;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          }

          @keyframes flyoutFadeIn {
            from {
              opacity: 0;
              transform: translateX(-6px);
            }
            to {
              opacity: 1;
              transform: translateX(0);
            }
          }
        `}</style>
      </div>,
      document.body
    );
  }

  return createPortal(
    <div
      ref={flyoutRef}
      className="sidebar-flyout-panel"
      style={{
        position: 'fixed',
        top: `${position.top}px`,
        left: `${position.left}px`,
      }}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="sidebar-flyout-header">
        <Icon size={20} />
        <span>{module.name}</span>
      </div>

      <div className="sidebar-flyout-list">
        {module.subItems.map((subItem, index) => {
          const isActive = isPathActive(subItem.path);
          const hasBadge = ['/purchase-requests/pending-approval','/purchase-requests/procurement','/purchase-requests/finance','/gmd/dashboard'].includes(subItem.path) && badgeCounts[subItem.path] > 0;
          
          return (
            <div
              key={index}
              className={`sidebar-flyout-item ${isActive ? 'active' : ''}`}
              onClick={() => handleSubItemClick(subItem.path)}
            >
              <span className="sidebar-flyout-label">{subItem.name}</span>
              {hasBadge && (
                <span className="sidebar-flyout-badge">
                  {badgeCounts[subItem.path]}
                </span>
              )}
            </div>
          );
        })}
      </div>

      <style>{`
        .sidebar-flyout-panel {
          width: 220px;
          max-height: 75vh;
          background: #1a2e45;
          border: 1px solid rgba(255, 255, 255, 0.08);
          border-radius: 0 14px 14px 14px;
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.4);
          z-index: 9999;
          pointer-events: auto;
          animation: flyoutSlideIn 160ms ease-out;
          overflow: hidden;
          display: flex;
          flex-direction: column;
        }

        @keyframes flyoutSlideIn {
          from {
            opacity: 0;
            transform: translateX(-6px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }

        .sidebar-flyout-header {
          display: flex;
          align-items: center;
          gap: 10px;
          padding: 14px 16px 12px;
          border-bottom: 1px solid rgba(255, 255, 255, 0.07);
          color: #E8EFF6;
          font-size: 13px;
          font-weight: 600;
        }

        .sidebar-flyout-header svg {
          color: #C8D6E8;
          flex-shrink: 0;
        }

        .sidebar-flyout-list {
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 8px;
          overflow-y: auto;
        }

        .sidebar-flyout-list::-webkit-scrollbar {
          width: 4px;
        }

        .sidebar-flyout-list::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-flyout-list::-webkit-scrollbar-thumb {
          background: rgba(223, 107, 56, 0.45);
          border-radius: 2px;
        }

        .sidebar-flyout-item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 10px 12px;
          border-radius: 8px;
          background: rgba(255, 255, 255, 0.04);
          border: 1px solid rgba(255, 255, 255, 0.06);
          cursor: pointer;
          transition: all 0.15s;
        }

        .sidebar-flyout-item:hover {
          background: rgba(255, 255, 255, 0.08);
          border-color: rgba(255, 255, 255, 0.12);
        }

        .sidebar-flyout-item.active {
          background: rgba(223, 107, 56, 0.15);
          border-color: rgba(223, 107, 56, 0.5);
          border-left: 2px solid #DF6B38;
          padding-left: 10px;
        }

        .sidebar-flyout-label {
          font-size: 12px;
          color: #8AAAC4;
          transition: color 0.15s;
        }

        .sidebar-flyout-item.active .sidebar-flyout-label {
          color: #DF6B38;
          font-weight: 500;
        }

        .sidebar-flyout-badge {
          padding: 2px 6px;
          border-radius: 10px;
          background: #DF6B38;
          color: white;
          font-size: 10px;
          font-weight: 700;
        }
      `}</style>
    </div>,
    document.body
  );
};

export default SidebarFlyout;
